import React from 'react';
import { TrendingUp, DollarSign, Users, ArrowUp, ArrowDown, Minus, AlertTriangle, Info } from 'lucide-react';
import { AporteSummary, AporteMesSnapshot } from '../services/aporteService';
import { Tooltip } from './ui/Tooltip';

function getConcentracionDisplay(pct: number): { colorClass: string; label: string } {
  if (pct >= 80) return { colorClass: 'text-red-600', label: 'Alta — grupo frágil' };
  if (pct >= 50) return { colorClass: 'text-amber-600', label: 'Media' };
  return { colorClass: 'text-green-600', label: 'Baja — grupo sano' };
}

const MESES_LARGO = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MESES_CORTO = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

type Tendencia = 'sube' | 'baja' | 'igual';

function getTendencia(actual: number, anterior: number): Tendencia {
  if (actual > anterior) return 'sube';
  if (actual < anterior) return 'baja';
  return 'igual';
}

interface AportesIndicadoresProps {
  resumen: AporteSummary | null;
  loading: boolean;
  periodo: { mes: number; año: number };
  historial: AporteMesSnapshot[];
}

export const AportesIndicadores: React.FC<AportesIndicadoresProps> = ({ resumen, loading, periodo, historial }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!resumen) {
    return (
      <div className="text-center py-12 text-slate-500">
        No hay datos de aportes disponibles
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-base font-semibold text-slate-700">
        {MESES_LARGO[periodo.mes - 1]} {periodo.año}
      </p>
      <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600 font-medium mb-1">Aportantes</p>
              <div className="space-y-0.5">
                <p className="text-sm text-slate-500">
                  Aporte mensual{' '}
                  <span className="text-base font-bold text-slate-800">
                    {resumen.aportantes_aporte_mensual} de {resumen.total_miembros}
                  </span>
                </p>
                <p className="text-sm text-slate-500">
                  Cuota iglesia{' '}
                  <span className="text-base font-bold text-slate-800">
                    {resumen.aportantes_cuota_iglesia} de {resumen.total_miembros}
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Adultos</p>
              <p className="text-lg font-bold text-slate-700">{resumen.total_adultos}</p>
            </div>
            <div className="w-px h-8 bg-slate-200" />
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Niños</p>
              <p className="text-lg font-bold text-slate-700">{resumen.total_ninos}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-right">
              <div className="flex items-center justify-end gap-1">
                <Tooltip content="Miembros que registraron al menos un aporte en el mes actual. Los meses anteriores muestran cómo ha variado la participación." position="bottom">
                  <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                </Tooltip>
                <p className="text-sm text-slate-600 font-medium">Con Aportes</p>
              </div>
              <p className="text-2xl font-bold text-slate-800">
                {historial.length > 0 ? historial[0].miembros_aportan : resumen.miembros_al_dia}
              </p>
            </div>
          </div>
          {historial.length > 1 && (
            <div className="flex items-end gap-3">
              {historial.slice(1, 7).map((snap, i) => {
                const anterior = historial[i + 2];
                const tendencia: Tendencia = anterior
                  ? getTendencia(snap.miembros_aportan, anterior.miembros_aportan)
                  : 'igual';
                return (
                  <div key={`${snap.año}-${snap.mes}`} className="flex flex-col items-center gap-0.5 opacity-45">
                    <span className="text-sm font-bold text-slate-600">{snap.miembros_aportan}</span>
                    {anterior ? (
                      <>
                        {tendencia === 'sube' && <ArrowUp className="w-3 h-3 text-green-500" />}
                        {tendencia === 'baja' && <ArrowDown className="w-3 h-3 text-red-500" />}
                        {tendencia === 'igual' && <Minus className="w-3 h-3 text-slate-400" />}
                      </>
                    ) : (
                      <span className="w-3 h-3" />
                    )}
                    <span className="text-xs text-slate-400">{MESES_CORTO[snap.mes - 1]}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600 font-medium">Aportes Mensuales</p>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(resumen.total_aportes_mensuales_recibidos)}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-0.5">Comprometido</p>
            <p className="text-lg font-bold text-slate-700">{formatCurrency(resumen.total_aporte_mensual_esperado)}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-amber-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-amber-600" />
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600 font-medium">Cuotas Iglesia</p>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(resumen.total_cuota_iglesia_recibida)}</p>
            </div>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-x-3">
            <div className="space-y-1">
              <p className="text-xs text-slate-400 font-medium mb-1">Aportado</p>
              <div className="flex justify-between items-center">
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-medium">JAL</span>
                <span className="text-xs text-slate-600 font-medium">{formatCurrency(resumen.total_cuota_iglesia_jal)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-medium">PA</span>
                <span className="text-xs text-slate-600 font-medium">{formatCurrency(resumen.total_cuota_iglesia_pa)}</span>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-400 font-medium mb-1">Comprometido</p>
              <div className="flex justify-between items-center">
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">JAL</span>
                <span className="text-xs text-slate-600 font-medium">{formatCurrency(resumen.total_cuota_iglesia_jal_esperada)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">PA</span>
                <span className="text-xs text-slate-600 font-medium">{formatCurrency(resumen.total_cuota_iglesia_pa_esperada)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className={`p-3 rounded-lg ${resumen.miembros_sin_aportar_2_meses > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
              <AlertTriangle className={`w-6 h-6 ${resumen.miembros_sin_aportar_2_meses > 0 ? 'text-red-600' : 'text-green-600'}`} />
            </div>
            <div className="text-right">
              <div className="flex items-center justify-end gap-1">
                <Tooltip content="Miembros sin ningún aporte en el mes actual ni en el anterior. Son quienes más necesitan seguimiento o contacto." position="bottom">
                  <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                </Tooltip>
                <p className="text-sm text-slate-600 font-medium">Sin Aportar 2+M</p>
              </div>
              <p className={`text-2xl font-bold ${resumen.miembros_sin_aportar_2_meses > 0 ? 'text-red-700' : 'text-green-700'}`}>
                {resumen.miembros_sin_aportar_2_meses}
              </p>
            </div>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-0.5">de {resumen.total_miembros} miembros</p>
            <p className="text-lg font-bold text-slate-700">
              {resumen.total_miembros > 0
                ? `${(((resumen.total_miembros - resumen.miembros_sin_aportar_2_meses) / resumen.total_miembros) * 100).toFixed(0)}% activos`
                : '—'}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-violet-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-violet-600" />
            </div>
            <div className="text-right">
              <div className="flex items-center justify-end gap-1">
                <Tooltip content="Qué porcentaje del total recaudado este mes viene del 20% que más aporta. Si es muy alto, el grupo depende de pocas personas y es frágil ante cambios." position="bottom">
                  <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                </Tooltip>
                <p className="text-sm text-slate-600 font-medium">Concentración</p>
              </div>
              <p className="text-2xl font-bold text-slate-800">{resumen.porcentaje_concentracion_top20.toFixed(0)}%</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-0.5">viene del top 20%</p>
            {(() => {
              const { colorClass, label } = getConcentracionDisplay(resumen.porcentaje_concentracion_top20);
              return <p className={`text-lg font-bold ${colorClass}`}>{label}</p>;
            })()}
          </div>
        </div>



      </div>
    </div>
  );
};
